import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const initialState = {
  createProductError: null,
  updateProductError: null,
  showProductError: null,
  uploadImageError: null,
  createProductInProgress: false,
  submittedProductId: null,
  redirectToListing: false,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateProductInProgress: false,
};

// ================ Async Thunks ================ //

export const requestShowProduct = createAsyncThunk(
  'app/EditProductPage/SHOW_PRODUCT',
  async (actionPayload, { dispatch, extra: sdk }) => {
    try {
      const response = await sdk.newSdk.catalogProducts.show(actionPayload);
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      throw storableError(e);
    }
  }
);

export const requestCreateProduct = createAsyncThunk(
  'app/EditProductPage/CREATE_PRODUCT',
  async (data, { dispatch, extra: sdk }) => {
    try {
      const queryParams = { expand: true, include: ['images'] };
      const response = await sdk.newSdk.catalogProducts.create(data, queryParams);
      
      dispatch(addMarketplaceEntities(response));
      
      const id = response.data.data.id.uuid;
      await dispatch(requestShowProduct({ id, include: ['images'] }));
      
      return response;
    } catch (e) {
      log.error(e, 'create-listing-draft-failed', { listingData: data });
      throw storableError(e);
    }
  }
);

export const requestImageUpload = createAsyncThunk(
  'app/EditProductPage/UPLOAD_IMAGE',
  async ({ id, file }, { extra: sdk }) => {
    try {
      const resp = await sdk.newSdk.images.upload({ file });
      return { id, imageId: resp.data.data.id, file };
    } catch (e) {
      throw { id, error: storableError(e) };
    }
  }
);

export const requestUpdateProduct = createAsyncThunk(
  'app/EditProductPage/UPDATE_PRODUCT',
  async (data, { dispatch, extra: sdk }) => {
    try {
      const { id, countryId, ...restData } = data;
      const response = await sdk.newSdk.catalogProducts.update({ id, ...restData });
      
      await dispatch(requestShowProduct({ id, include: ['images'] }));
      return response;
    } catch (e) {
      log.error(e, 'update-listing-failed', { listingData: data });
      throw storableError(e);
    }
  }
);

export const loadData = params => (dispatch) => {
  dispatch(resetPageState());
  const { id, type } = params;

  if (type === 'new') {
    return Promise.all([dispatch(fetchCurrentUser())]);
  }

  return Promise.all([
    dispatch(requestShowProduct({ id, include: ['images'] })),
    dispatch(fetchCurrentUser()),
  ]);
};

// ================ Slice ================ //

const editProductPageSlice = createSlice({
  name: 'app/EditProductPage',
  initialState,
  reducers: {
    markTabUpdated: (state, action) => {
      state.updatedTab = action.payload;
    },
    clearUpdatedTab: state => {
      state.updatedTab = null;
      state.updateProductError = null;
    },
    resetPageState: () => initialState,
    updateImageOrder: (state, action) => {
      const payload = action.payload?.imageOrder ? action.payload : { imageOrder: action.payload };
      state.imageOrder = payload.imageOrder;
    },
    removeProductImage: (state, action) => {
      const id = action.payload?.imageId || action.payload;
      if (!state.images[id]) {
        state.removedImageIds.push(id);
      }
      delete state.images[id];
      state.imageOrder = state.imageOrder.filter(i => i !== id);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(requestCreateProduct.pending, state => {
        state.createProductInProgress = true;
        state.createProductError = null;
        state.submittedProductId = null;
        state.listingDraft = null;
      })
      .addCase(requestCreateProduct.fulfilled, (state, action) => {
        state.createProductInProgress = false;
        state.submittedProductId = action.payload.data.data.id;
        state.listingDraft = action.payload.data;
      })
      .addCase(requestCreateProduct.rejected, (state, action) => {
        state.createProductInProgress = false;
        state.createProductError = action.error;
      })
 
      .addCase(requestUpdateProduct.pending, state => {
        state.updateProductInProgress = true;
        state.updateProductError = null;
      })
      .addCase(requestUpdateProduct.fulfilled, state => {
        state.updateProductInProgress = false;
      })
      .addCase(requestUpdateProduct.rejected, (state, action) => {
        state.updateProductInProgress = false;
        state.updateProductError = action.error;
      })

      .addCase(requestShowProduct.pending, state => {
        state.showProductError = null;
      })
      .addCase(requestShowProduct.fulfilled, state => {
        return { ...initialState, availabilityCalendar: { ...state.availabilityCalendar } };
      })
      .addCase(requestShowProduct.rejected, (state, action) => {
        console.error(action.error);
        state.showProductError = action.error;
        state.redirectToListing = false;
      })

      .addCase(requestImageUpload.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.images[id] = { id, file };
        state.imageOrder.push(id);
        state.uploadImageError = null;
      })
      .addCase(requestImageUpload.fulfilled, (state, action) => {
        const { id, imageId, file } = action.payload;
        state.images[id] = { id, imageId, file };
      })
      .addCase(requestImageUpload.rejected, (state, action) => {
        const { id, error } = action.error;
        state.imageOrder = state.imageOrder.filter(i => i !== id);
        delete state.images[id];
        state.uploadImageError = error;
      });
  },
});

export const {
  markTabUpdated,
  clearUpdatedTab,
  updateImageOrder,
  removeProductImage,
  resetPageState,
} = editProductPageSlice.actions;

export default editProductPageSlice.reducer;
